import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useSnackbar } from "notistack";
import { useAuth } from "../../hooks/useAuth";
import { login_user_api } from "../../DAL/Users";
import SubmitButton from "../../Components/GeneralComponents/SubmitButton";
import VaultSecureLogo from "../../Components/GeneralComponents/VaultSecureLogo";

const LoginPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { setUserInfo } = useAuth();
  const [passwordType, setPasswordType] = useState("password");
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChangeIcon = () => {
    let password_type = "password";
    if (passwordType === "password") {
      password_type = "text";
    }
    setPasswordType(password_type);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const result = await login_user_api(formData);
      
      if (result.code === 200 && result.status === true) {
        const login_data = result.data;
        localStorage.setItem("token", login_data.access_token);
        localStorage.setItem("user_info", JSON.stringify(login_data.user));
        setUserInfo(login_data.user);
        enqueueSnackbar(result.message || "Login successful", { variant: "success" });
        navigate(`/home`);
      } else {
        const errorMessage = result.message || "Login failed. Please check your credentials.";
        enqueueSnackbar(errorMessage, { variant: "error" });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Login error:", error);
      enqueueSnackbar("An error occurred during login. Please try again.", { variant: "error" });
      setIsLoading(false);
    }
  };

  const handleForgot = () => navigate(`/forgot-password`);

  return (
    <div className="login-card">
      {/* Left Panel - Dark Side with Branding */}
      <div className="login-left-panel">
        <div className="login-logo-section">
          <VaultSecureLogo className="logo-icon" color="#137fec" />
          <h1>VaultSecure</h1>
        </div>

        <div className="login-branding">
          <h2>
            Enterprise-Grade Security for Your <span className="partner-portal-highlight">Partner Portal</span>.
          </h2>
          <p>
            Securely manage, monitor, and grow your partnership with confidence
            and ease. Access powerful tools designed for enterprise partners.
          </p>
        </div>

        <div className="login-security-badge">
          <Icon icon="mdi:lock" className="lock-icon" />
          <span>256-bit AES Encryption</span>
        </div>
      </div>

      {/* Right Panel - Form Side */}
      <div className="login-right-panel">
        <div className="login-form-container">
          {/* Mobile Logo - Only visible on small screens */}
          <div className="mobile-logo">
            <VaultSecureLogo className="mobile-logo-icon" color="#137fec" />
          </div>

          <div className="login-header">
            <h1>Log in to your account</h1>
            <h5>Welcome back! Please enter your details.</h5>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className={errors.email ? "has-error" : ""}
                  autoComplete="email"
                  autoFocus
                />
              </div>
              {errors.email && (
                <span className="error-message">
                  <Icon icon="mdi:alert-circle" className="error-icon" />
                  {errors.email}
                </span>
              )}
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <input
                  type={passwordType}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className={errors.password ? "has-error" : ""}
                  autoComplete="current-password"
                />
                <Icon
                  icon={`mdi:eye${passwordType === "password" ? "-off" : ""}`}
                  className="eye-icon"
                  onClick={handleChangeIcon}
                />
              </div>
              {errors.password && (
                <span className="error-message">
                  <Icon icon="mdi:alert-circle" className="error-icon" />
                  {errors.password}
                </span>
              )}
            </div>

            {/* Remember Me and Forgot Password Row */}
            <div className="remember-forgot-row">
              <div className="remember-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="rememberMe">Remember for 30 days</label>
              </div>
              <div className="forget-password">
                <span className="login-forget-password" onClick={handleForgot}>
                  Forgot password?
                </span>
              </div>
            </div>

            <SubmitButton
              isLoading={isLoading}
              btnText="Log In"
              loadingText="Signing in..."
            />
          </form>

        </div>
      </div>
    </div>
  );
};

export default LoginPage;
